import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from '../reduxStore/Store';
import { deleteDocumentVente, duplicateDocumentVente, fetchDocumentVenteData, setSelectedDocument, setFilters, clearDocumentData, clearMessages } from '../reduxStore/documentVenteSlice';
import { fetchSysTypeDocuments } from '../reduxStore/SysTypeDocumentSlice';
import MyTable from '../components/MyTable';
import { TableAction, TableHeader } from '../constants/interfaces';
import { Document_vente } from '../models/models';
import { ButtonType, FormatRapport, TypeRapport } from '../constants/enums';
import { formatStringDate, formatNumberWithSpaces } from '../utils/utils';
import { labels } from '../constants/labels';
import { useSnackbar } from '../components/SnackbarProvider';
import DocumentVenteFilter from '../components/Filters/DocumentVenteFilter';
import { useGenerateReport } from '../hooks/useGenerateReport';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import PrintMenuButton from '../components/PrintMenuButton';
import DuplicateDocumentDrawer, { DuplicatePayload } from '../components/DuplicateDocumentDrawer';
import WorkflowStatutBadge from '../components/WorkflowStatutBadge';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AppPage from '../components/layout/AppPage';
import AppSurface from '../components/layout/AppSurface';
import AppPageHeader from '../components/layout/AppPageHeader';
import AppFilters from '../components/layout/AppFilters';

// Clé de génération pour le rapport LISTE (distincte des clés par document = numero_doc)
const LIST_REPORT_KEY = '__liste_doc_vente__';

/**
 * Liste des documents de vente — page dédiée, séparée du détail/édition
 * (routes /facturation/nouveau et /facturation/:id). "Modifier" et "Créer"
 * naviguent vers ces routes dédiées au lieu de basculer un état local.
 */
const DocumentVenteListPage: React.FC = () => {
  // totalHtCumul / totalTtcCumul : cumul des pages 1 → page courante (calculé par la SP)
  const { documentData, filters, total, totalHtCumul, totalTtcCumul, loading, error, successMessage } = useSelector((state: any) => state.DocumentVenteSlice);
  const totalTvaCumul = (totalTtcCumul ?? 0) - (totalHtCumul ?? 0);
  const { selectedTypeDoc, sysTypeData } = useSelector((state: any) => state.SysTypeDocumentSlice);
  const { selectedDepartement, isAllMode } = useSelector((state: any) => state.AppSlice);
  const permissions        = useSelector((state: RootState) => state.AuthSlice.permissions) ?? [];

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { generateReport, generating, generatingKey } = useGenerateReport();
  const duplicating = useSelector((state: any) => state.DocumentVenteSlice.duplicating);
  const [duplicateSource, setDuplicateSource] = useState<Document_vente | null>(null);

  // Types de document que l'utilisateur a le droit de créer — sert au drawer "Dupliquer"
  // (même filtre que NewDocumentSpeedDial, un doublon assumé : composants indépendants).
  const creatableTypes = useMemo(
    () => (sysTypeData ?? []).filter((t: any) => permissions.includes(`documentVentes.${t.code}.create`)),
    [sysTypeData, permissions],
  );

  useEffect(() => {
    if (!sysTypeData || sysTypeData.length === 0) dispatch(fetchSysTypeDocuments());
  }, [sysTypeData, dispatch]);

  // Debounce 400ms : des clics de pagination rapprochés ne déclenchent qu'un seul
  // appel, pour la dernière page demandée.
  const debouncedFilters = useDebouncedValue(filters, 400);

  useEffect(() => {
    const request = dispatch(fetchDocumentVenteData(debouncedFilters));
    // Nouvelle page/filtre ou démontage : la requête en cours est obsolète → annulée,
    // sa réponse ne peut plus écraser celle de la dernière page demandée.
    return () => request.abort();
  }, [debouncedFilters, dispatch, successMessage]);

  // Squelette dès le clic (pendant le debounce) et jusqu'à la réponse serveur.
  const tableLoading = loading || filters !== debouncedFilters;

  // Remonte les erreurs du slice (fetch…) dans le snackbar global. `error` n'est
  // PAS une dépendance du fetch ci-dessus → on peut le clear après affichage sans
  // déclencher de re-fetch. Le clear permet aussi de réafficher une erreur
  // identique consécutive (null → msg relance l'effet).
  useEffect(() => {
    if (error) {
      showSnackbar(error, 'error');
      dispatch(clearMessages());
    }
  }, [error, showSnackbar, dispatch]);

  // Pas de `resetFilter()` ici : cette liste se démonte à CHAQUE navigation vers le
  // détail (/facturation/:id) — resetFilter() y effacerait la pagination/les filtres
  // à chaque clic sur "Modifier", pas seulement en quittant le module.
  useEffect(() => {
    return () => {
      dispatch(setSelectedDocument(null));
      dispatch(clearDocumentData());
    };
  }, [dispatch]);

  const handleEdit = (doc: Document_vente) => {
    if (doc) navigate(`/facturation/${doc.id}`);
  };

  const handlePageChange = (newPage: number, newRowsPerPage: number) => {
    dispatch(setFilters({
      ...filters,
      offset: newPage * newRowsPerPage,
      limit: newRowsPerPage,
    }));
  };

  const handlePrint = (doc: Document_vente, format: FormatRapport, template_id?: number | null) => {
    if (!doc.numero_doc) return;
    generateReport({
      type_rapport:   TypeRapport.IMPRESSION_DOCUMENT,
      format,
      parametres:     {
        numero_doc:         doc.numero_doc,
        document_id:        doc.id ?? null,
        departement_id:     doc.departement_id ?? null,
        type_document_code: doc.type_document?.code ?? null,
      },
      template_id:    template_id ?? null,
      departement_id: selectedDepartement?.id ?? null,
      fileName:       doc.numero_doc,
      key:            doc.numero_doc,
    });
  };

  // Duplique le document ouvert dans le drawer avec l'en-tête réglé par l'utilisateur
  // (type/tiers/dépôt/référence/date) — l'en-tête et les lignes sont recréés côté backend
  // en une transaction, puis on ouvre directement le nouveau document pour relecture.
  const handleDuplicateSubmit = async (payload: DuplicatePayload) => {
    if (!duplicateSource) return;
    try {
      const created = await dispatch(duplicateDocumentVente({ source_id: duplicateSource.id, ...payload })).unwrap();
      // Avertissements non bloquants (ex : remises / frais non copiés vers un BL / BRF).
      if (created.warnings?.length) {
        showSnackbar(`Document dupliqué. ${created.warnings.join(' ')}`, 'warning');
      } else {
        showSnackbar('Document dupliqué avec succès !', 'success');
      }
      setDuplicateSource(null);
      navigate(`/facturation/${created.id}`);
    } catch (error) {
      showSnackbar((error as string) || 'Erreur lors de la duplication', 'error');
    }
  };

  // Impression de la LISTE des documents (rapport DOCUMENT_VENTE) avec les filtres courants.
  const handleGenerateListReport = (format: FormatRapport) => {
    const { limit, offset, ...filterParams } = filters;
    generateReport({
      type_rapport:   TypeRapport.DOCUMENT_VENTE,
      format,
      parametres:     { ...filterParams, departement_id: filterParams.departement_id ?? selectedDepartement?.id ?? null },
      departement_id: selectedDepartement?.id ?? null,
      fileName:       `documents_vente_${format.toLowerCase()}`,
      key:            LIST_REPORT_KEY,
    });
  };

  const headers: TableHeader<Document_vente>[] = [
    { key: "numero_doc",    label: "N°Document" },
    // Visible seulement en mode "Tous départements" : dans un contexte de département
    // actif, toutes les lignes appartiennent déjà au même département (redondant).
    ...(isAllMode ? [{
      key: "departement_nom" as const,
      label: "Département",
      render: (row: Document_vente) => row.departement_nom ?? '—',
    }] : []),
    { key: "tiers",         label: "Tiers",          render: (row) => row.tiers ? `${row.tiers.nom ?? ''} ${row.tiers.prenom ?? ''}`.trim() : '' },
    { key: "date_doc",      label: "Date",           render: (row) => formatStringDate(row.date_doc || '') },
    // hideBelow : colonnes secondaires retirées sur écran réduit (restent dans le détail).
    { key: "date_echeance", label: "Date Echeance",  render: (row) => formatStringDate(row.date_echeance || ''), hideBelow: 'lg' },
    { key: "reference",     label: "Reference",      hideBelow: 'md' },
    { key: "total_ht",      label: "Total HT",       align: 'right', minWidth: 130, hideBelow: 'md' },
    { key: "total_ttc",     label: "Total TTC",      align: 'right', minWidth: 130 },
    { key: "statut",        label: "Statut",         render: (row) => <WorkflowStatutBadge statut={row.statut} />, minWidth: 130 },
    {
      key: "cloture",
      label: "Clôture",
      align: "center",
      width: 70,
      // Comparaison truthy (cf. DocumentVente.tsx) — `cloture` peut être typé
      // différemment selon la SP/driver, on tolère Number/BigInt/Boolean.
      render: (row) => row.cloture
        ? (
          <Tooltip title="Clôturé" placement="top">
            <LockIcon fontSize="small" color="success" />
          </Tooltip>
        )
        : null,
    },
  ];

  const tableActions: TableAction<Document_vente>[] = [
    {
      label: "Dupliquer",
      hidden: () => creatableTypes.length === 0,
      render: (row) => (
        <Tooltip title="Dupliquer">
          <IconButton size="small" onClick={() => setDuplicateSource(row)}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      label: "Imprimer",
      render: (row) => (
        <PrintMenuButton
          onPdf={(tid)   => handlePrint(row, FormatRapport.PDF, tid)}
          onExcel={(tid) => handlePrint(row, FormatRapport.EXCEL, tid)}
          loading={generatingKey === row.numero_doc}
          disabled={generating && generatingKey !== row.numero_doc}
          typeRapport={TypeRapport.IMPRESSION_DOCUMENT}
          typeDocumentCode={row.type_document?.code ?? null}
        />
      ),
    },
    {
      label: "Edit",
      callback: handleEdit,
      type: ButtonType.EDIT,
      hidden: (row) => !permissions.includes(`documentVentes.${row.type_document?.code}.update`),
    },
    {
      label: "Delete",
      callback: async (doc) => {
        if (doc && window.confirm('Voulez-vous vraiment supprimer ce document ?')) {
          try {
            await dispatch(deleteDocumentVente({
              document_vente_id: doc.id!,
              numero_doc: doc.numero_doc,
              doc_code: doc.type_document?.code ?? '',
            })).unwrap();
            showSnackbar('Document supprimé avec succès !', 'success');
          } catch (error) {
            showSnackbar((error as string) || 'Erreur lors de la suppression', 'error');
          }
        }
      },
      type: ButtonType.DELETE,
      hidden: (row) => !permissions.includes(`documentVentes.${row.type_document?.code}.delete`),
    },
  ];

  return (
    <AppPage maxWidth={false}>
      <AppPageHeader
        title={labels.moduleFacturation ?? 'Facturation'}
        subtitle={`${total} document${total > 1 ? 's' : ''}`}
        icon={<ReceiptLongIcon />}
        addLabel={selectedTypeDoc ? `Créer ${selectedTypeDoc.libelle}` : undefined}
        onAdd={selectedTypeDoc ? () => navigate(`/facturation/nouveau?type=${selectedTypeDoc.code}`) : undefined}
        actions={
          <PrintMenuButton
            onPdf={()   => handleGenerateListReport(FormatRapport.PDF)}
            onExcel={() => handleGenerateListReport(FormatRapport.EXCEL)}
            loading={generating && generatingKey === LIST_REPORT_KEY}
            disabled={generating && generatingKey !== LIST_REPORT_KEY}
          />
        }
      />

      {error && <Typography color="#b42318" mb={2}>Erreur : {error}</Typography>}

      <AppSurface padding={0}>
        <AppFilters>
          <DocumentVenteFilter />
        </AppFilters>
        <MyTable
          headers={headers}
          data={documentData}
          onPageChange={handlePageChange}
          actions={tableActions}
          onRowClick={handleEdit}
          totalRows={total}
          loading={tableLoading}
          tableKey="facturation_documents"
          page={Math.floor((filters.offset ?? 0) / (filters.limit || 5))}
          pageSize={filters.limit ?? 5}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', p: 1.5, borderTop: '1px solid #e4e7ec' }}>
          <Chip label={`Total HT : ${formatNumberWithSpaces(totalHtCumul)}`} variant="outlined" size="medium" />
          <Chip label={`TVA : ${formatNumberWithSpaces(totalTvaCumul)}`} variant="outlined" size="medium" />
          <Chip label={`Total TTC : ${formatNumberWithSpaces(totalTtcCumul)}`} color="primary" variant="filled" size="medium" />
        </Box>
      </AppSurface>

      <DuplicateDocumentDrawer
        open={!!duplicateSource}
        onClose={() => setDuplicateSource(null)}
        source={duplicateSource}
        types={creatableTypes}
        onSubmit={handleDuplicateSubmit}
        submitting={duplicating}
      />
    </AppPage>
  );
};

export default DocumentVenteListPage;
